export type JourneyEvidenceStatus = "available" | "missing";

interface JobJourneyState {
  session_id: string;
  viewed_job_ids: string[];
  total_job_views: number;
  first_viewed_at_by_job: Record<string, number>;
  apply_count: number;
}

export interface JobJourneyFacts {
  frontend_session_id?: string;
  journey_evidence_status: JourneyEvidenceStatus;
  jobs_viewed_in_session: number;
  job_views_in_session: number;
  time_to_apply_ms?: number;
  apply_sequence: number;
}

export interface JobApplyJourneyFacts extends JobJourneyFacts {
  job_viewed_before_apply: boolean;
}

const STORAGE_PREFIX = "acg_job_journey_v1:";
const MAX_SESSION_ID_LENGTH = 128;
const MAX_JOB_IDS = 500;
const MAX_TIME_TO_APPLY_MS = 7 * 24 * 60 * 60 * 1000;

export function getPostHogSessionId(posthog: unknown): string | undefined {
  if (!posthog || typeof posthog !== "object") return undefined;
  const getter = (posthog as { get_session_id?: () => unknown }).get_session_id;
  if (typeof getter !== "function") return undefined;
  try {
    const value = getter.call(posthog);
    if (typeof value !== "string") return undefined;
    const normalized = value.trim();
    return normalized ? normalized.slice(0, MAX_SESSION_ID_LENGTH) : undefined;
  } catch {
    return undefined;
  }
}

export function recordJobView(
  storage: Storage | undefined,
  sessionId: string | undefined,
  jobId: string,
  nowMs: number = Date.now(),
): JobJourneyFacts {
  if (!storage || !sessionId) return missingFacts();

  const state = readState(storage, sessionId);
  const alreadyViewed = state.viewed_job_ids.includes(jobId);
  const canTrackJob =
    alreadyViewed || state.viewed_job_ids.length < MAX_JOB_IDS;
  if (!alreadyViewed && canTrackJob) {
    state.viewed_job_ids.push(jobId);
  }
  state.total_job_views += 1;
  if (canTrackJob) {
    state.first_viewed_at_by_job[jobId] ??= nowMs;
  }

  if (!writeState(storage, state)) return missingFacts();
  return factsFromState(state, jobId, nowMs, false);
}

export function recordJobApply(
  storage: Storage | undefined,
  sessionId: string | undefined,
  jobId: string,
  nowMs: number = Date.now(),
): JobApplyJourneyFacts {
  if (!storage || !sessionId) return missingApplyFacts();

  const state = readState(storage, sessionId);
  state.apply_count += 1;
  if (!writeState(storage, state)) return missingApplyFacts();
  return {
    ...factsFromState(state, jobId, nowMs, true),
    job_viewed_before_apply: state.viewed_job_ids.includes(jobId),
  };
}

function readState(storage: Storage, sessionId: string): JobJourneyState {
  const empty = emptyState(sessionId);
  try {
    const raw = storage.getItem(storageKey(sessionId));
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<JobJourneyState>;
    if (parsed.session_id !== sessionId) return empty;
    const viewedJobIds = Array.isArray(parsed.viewed_job_ids)
      ? parsed.viewed_job_ids
          .filter((value): value is string => typeof value === "string")
          .slice(0, MAX_JOB_IDS)
      : [];
    const rawFirstViewedAt =
      parsed.first_viewed_at_by_job &&
      typeof parsed.first_viewed_at_by_job === "object"
        ? parsed.first_viewed_at_by_job
        : {};
    const firstViewedAtByJob = Object.fromEntries(
      viewedJobIds.flatMap((jobId) => {
        const value = rawFirstViewedAt[jobId];
        return typeof value === "number" && Number.isFinite(value)
          ? [[jobId, value]]
          : [];
      }),
    );
    return {
      session_id: sessionId,
      viewed_job_ids: viewedJobIds,
      total_job_views: validCounter(parsed.total_job_views),
      first_viewed_at_by_job: firstViewedAtByJob,
      apply_count: validCounter(parsed.apply_count),
    };
  } catch {
    return empty;
  }
}

function writeState(storage: Storage, state: JobJourneyState): boolean {
  try {
    const currentKey = storageKey(state.session_id);
    const staleKeys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(STORAGE_PREFIX) && key !== currentKey) {
        staleKeys.push(key);
      }
    }
    staleKeys.forEach((key) => storage.removeItem(key));
    storage.setItem(currentKey, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

function factsFromState(
  state: JobJourneyState,
  jobId: string,
  nowMs: number,
  includeApplySequence: boolean,
): JobJourneyFacts {
  const firstViewedAt = state.first_viewed_at_by_job[jobId];
  const rawTimeToApply =
    typeof firstViewedAt === "number" ? nowMs - firstViewedAt : undefined;
  const timeToApplyMs =
    rawTimeToApply !== undefined &&
    rawTimeToApply >= 0 &&
    rawTimeToApply <= MAX_TIME_TO_APPLY_MS
      ? rawTimeToApply
      : undefined;

  return {
    frontend_session_id: state.session_id,
    journey_evidence_status: "available",
    jobs_viewed_in_session: state.viewed_job_ids.length,
    job_views_in_session: state.total_job_views,
    ...(timeToApplyMs !== undefined && { time_to_apply_ms: timeToApplyMs }),
    apply_sequence: includeApplySequence ? state.apply_count : 0,
  };
}

function emptyState(sessionId: string): JobJourneyState {
  return {
    session_id: sessionId,
    viewed_job_ids: [],
    total_job_views: 0,
    first_viewed_at_by_job: {},
    apply_count: 0,
  };
}

function missingFacts(): JobJourneyFacts {
  return {
    journey_evidence_status: "missing",
    jobs_viewed_in_session: 0,
    job_views_in_session: 0,
    apply_sequence: 0,
  };
}

function missingApplyFacts(): JobApplyJourneyFacts {
  return {
    ...missingFacts(),
    job_viewed_before_apply: false,
  };
}

function storageKey(sessionId: string): string {
  return `${STORAGE_PREFIX}${sessionId}`;
}

function validCounter(value: unknown): number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? value
    : 0;
}
